import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import {
  EngagementSurveyRollupType,
  ExportEngagementSurveyToCsvMutationMutation,
  ExportEngagementSurveyToCsvMutationMutationVariables,
  GetComplianceProgramEngagementSurveyReportQuery,
  GetComplianceProgramEngagementSurveyReportQueryVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import {
  currentUserVar,
  isAdminVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import exportEngagementSurveyCsvMutation from "../graphql/export-engagement-survey-csv-mutation";
import getComplianceProgramEngagementSurveyReportQuery from "../graphql/get-compliance-program-engagement-survey-report-query";
import EngagementSurveyReportTable from "./engagement-survey-report/engagement-survey-report-table";
import EngagementSurveyReportViewOptionsSidebar from "./engagement-survey-report/engagement-survey-view-options-sidebar";
import {
  getFilterByType,
  initEngagementSurveyReportViewOptions,
  npsColors,
  rangeColors,
} from "./engagement-survey-report/helpers";
import {
  EngagementSurveyReportFilterType,
  EngagementSurveyReportTableType,
  EngagementSurveyReportViewOptions,
} from "./engagement-survey-report/types";

export const stickyColWidth = "w-92 min-w-92 max-w-92";
export const resultColWidth = "w-48 max-w-48 min-w-48";

const EngagementSurveyReport = ({
  complianceProgramId,
  headerContent,
  isInReporting,
}: {
  complianceProgramId: number;
  headerContent?: JSX.Element;
  isInReporting?: boolean;
}) => {
  const isAdmin = isAdminVar();
  const label = useLabel();
  const currentUser = currentUserVar();
  const [showViewOptionsSidebar, setShowViewOptionsSidebar] = useState(false);
  const [viewOptions, setViewOptions] =
    useState<EngagementSurveyReportViewOptions>(
      initEngagementSurveyReportViewOptions,
    );

  const {
    teamIdsFilter,
    managerIdsFilter,
    managerIdsFullTreeFilter,
    employeeStartDateRangeFilter,
    locationsFilter,
  } = useMemo(() => {
    const teamIdsFilter = getFilterByType(
      viewOptions.filters.list,
      EngagementSurveyReportFilterType.TEAM,
    );
    const managerIdsFilter = getFilterByType(
      viewOptions.filters.list,
      EngagementSurveyReportFilterType.REPORTS_TO,
    );
    const managerIdsFullTreeFilter = getFilterByType(
      viewOptions.filters.list,
      EngagementSurveyReportFilterType.REPORTS_TO_FULL_TREE,
    );
    const employeeStartDateRangeFilter = getFilterByType(
      viewOptions.filters.list,
      EngagementSurveyReportFilterType.EMPLOYEE_START_DATE,
    );
    const locationsFilter = getFilterByType(
      viewOptions.filters.list,
      EngagementSurveyReportFilterType.LOCATION,
    );
    return {
      teamIdsFilter: teamIdsFilter ? teamIdsFilter.map(({ id }) => id) : [],
      managerIdsFilter: managerIdsFilter
        ? managerIdsFilter.map(({ id }) => id)
        : [],
      managerIdsFullTreeFilter: managerIdsFullTreeFilter
        ? managerIdsFullTreeFilter.map(({ id }) => id)
        : [],
      employeeStartDateRangeFilter: employeeStartDateRangeFilter
        ? employeeStartDateRangeFilter
        : [null, null],
      locationsFilter: locationsFilter ? locationsFilter : [],
    };
  }, [viewOptions.filters.list]);

  const {
    data: programData,
    loading: isLoadingProgram,
    previousData: previousProgramData,
  } = useQuery<
    GetComplianceProgramEngagementSurveyReportQuery,
    GetComplianceProgramEngagementSurveyReportQueryVariables
  >(getComplianceProgramEngagementSurveyReportQuery, {
    variables: {
      complianceProgramId,
      groupBy: viewOptions.groupBy,
      teamIds: teamIdsFilter,
      managerIds: managerIdsFilter,
      managerIdsFullTree: managerIdsFullTreeFilter,
      employeeStartDateRange: employeeStartDateRangeFilter,
      locations: locationsFilter,
      collapsedKeys: viewOptions.hiddenColumns,
    },
  });

  const [exportToCSV, { loading: isExporting }] = useMutation<
    ExportEngagementSurveyToCsvMutationMutation,
    ExportEngagementSurveyToCsvMutationMutationVariables
  >(exportEngagementSurveyCsvMutation);

  const complianceProgram = useMemo(
    () =>
      programData
        ? programData.complianceProgram
        : previousProgramData
          ? previousProgramData.complianceProgram
          : null,
    [programData, previousProgramData],
  );

  const exportProgram = useCallback(() => {
    if (!complianceProgram?.engagementSurveyReport || isExporting) {
      return;
    }
    exportToCSV({
      variables: {
        complianceProgramId,
        groupBy: viewOptions.groupBy,
        teamIds: teamIdsFilter,
        managerIds: managerIdsFilter,
        managerIdsFullTree: managerIdsFullTreeFilter,
        employeeStartDateRange: employeeStartDateRangeFilter,
        locations: locationsFilter,
        collapsedKeys: viewOptions.hiddenColumns,
      },
    });
    successNotificationVar({
      title: `CSV export will be emailed to ${currentUser.email}. The link will expire in two hours.`,
    });
  }, [
    complianceProgram,
    isExporting,
    exportToCSV,
    complianceProgramId,
    viewOptions,
    teamIdsFilter,
    managerIdsFilter,
    managerIdsFullTreeFilter,
    employeeStartDateRangeFilter,
    locationsFilter,
    currentUser,
  ]);

  const responders = useMemo(() => {
    return complianceProgram?.engagementSurveyReport
      ? complianceProgram.engagementSurveyReport?.responders.edges
          .map((edge) => assertNonNull(edge?.node))
          .filter(({ responderCount }) => responderCount > 0)
      : [];
  }, [complianceProgram]);

  const hasResponses = useMemo(() => {
    const orgRollup = responders.find(
      ({ rollupType }) =>
        rollupType === EngagementSurveyRollupType.Organization,
    );
    return orgRollup && orgRollup.responderCount > 0;
  }, [responders]);

  const npsQuestions = useMemo(() => {
    return complianceProgram?.engagementSurveyReport
      ? complianceProgram.engagementSurveyReport?.npsQuestionRows.edges.map(
          (edge) => assertNonNull(edge?.node),
        )
      : [];
  }, [complianceProgram]);
  const rangeQuestions = useMemo(() => {
    return complianceProgram?.engagementSurveyReport
      ? complianceProgram.engagementSurveyReport?.rangeQuestionRows.edges.map(
          (edge) => assertNonNull(edge?.node),
        )
      : [];
  }, [complianceProgram]);

  const handleUpdateViewOptions = useCallback(
    (newOptions: Partial<EngagementSurveyReportViewOptions>) => {
      setViewOptions((prev) => ({
        ...prev,
        ...newOptions,
      }));
    },
    [],
  );

  if (
    !isLoadingProgram &&
    npsQuestions.length === 0 &&
    rangeQuestions.length === 0
  ) {
    return (
      <Layout.MainSubSectionEmpty>
        <div className="flex items-center justify-center gap-2">
          <TopicflowIcon type="info" className="h-4 w-4" />
          <div>
            There are no range or NPS questions in this survey. To view all the
            responses,{" "}
            <AppLink
              className="text-blue-600 hover:underline"
              to={`/survey/delivery/${complianceProgram?.assessmentDeliveries?.edges[0]?.node?.id}`}
            >
              view the full survey here
            </AppLink>
            .
          </div>
        </div>
      </Layout.MainSubSectionEmpty>
    );
  }

  return (
    <div
      className={classNames(
        "pb-6 flex flex-col gap-6",
        isInReporting && "px-8",
      )}
    >
      {showViewOptionsSidebar && (
        <EngagementSurveyReportViewOptionsSidebar
          viewOptions={viewOptions}
          onUpdateViewOptions={handleUpdateViewOptions}
          onClose={() => setShowViewOptionsSidebar(false)}
          responders={responders}
        />
      )}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">{headerContent}</div>

        <div className="flex items-center gap-3">
          {!isLoadingProgram &&
            !isAdmin &&
            !complianceProgram?.hasAccessToAppliesToUsers && (
              <Tooltip
                text={`You do not have the access privileges to view all the responses within the ${label(
                  "organization",
                )}.`}
              >
                <div className="text-sm bg-amber-100 py-2 px-3 rounded-md flex items-center gap-2">
                  <TopicflowIcon
                    type="warning"
                    className="text-amber-700 h-5 w-5"
                  />
                  <div>Not all responses are shown</div>
                </div>
              </Tooltip>
            )}
          {!isLoadingProgram && hasResponses && (
            <Button
              size={ButtonSize.large}
              onClick={exportProgram}
              text="Export CSV"
            />
          )}
          {!isLoadingProgram &&
            complianceProgram?.assessmentDeliveries?.edges &&
            complianceProgram?.assessmentDeliveries.edges.length > 0 && (
              <Button
                size={ButtonSize.large}
                to={`/survey/delivery/${complianceProgram?.assessmentDeliveries.edges[0]?.node?.id}`}
                text="View Full Data"
              />
            )}
          <Button
            disabled={isLoadingProgram}
            className="flex items-center"
            theme={buttonTheme.iconGray}
            onClick={() => setShowViewOptionsSidebar(true)}
            aria-label="View Options"
          >
            <TopicflowIcon type="adjustments" className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isLoadingProgram && (
        <div className="flex-1 flex justify-center">
          <Loading />
        </div>
      )}
      {!isLoadingProgram && !hasResponses && (
        <Layout.MainSubSectionEmpty>
          There are no responses to the survey.
        </Layout.MainSubSectionEmpty>
      )}
      {hasResponses && npsQuestions.length > 0 && !isLoadingProgram && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm font-semibold">eNPS Results</div>
            <div className="flex items-center gap-2">
              <div className="text-xs">Low</div>
              {npsColors.map((color) => (
                <div
                  key={color}
                  className="h-3 w-3 rounded-sm"
                  style={{ backgroundColor: color }}
                />
              ))}
              <div className="text-xs">High</div>
            </div>
          </div>
          <EngagementSurveyReportTable
            responders={responders}
            questionRows={npsQuestions}
            type={EngagementSurveyReportTableType.NPS}
          />
        </div>
      )}
      {hasResponses && rangeQuestions.length > 0 && !isLoadingProgram && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm font-semibold">Range Question Results</div>
            <div className="flex items-center gap-2">
              <div className="text-xs">Low</div>
              {rangeColors.map((color) => (
                <div
                  key={color}
                  className="h-3 w-3 rounded-sm"
                  style={{ backgroundColor: color }}
                />
              ))}
              <div className="text-xs">High</div>
            </div>
          </div>
          <EngagementSurveyReportTable
            responders={responders}
            questionRows={rangeQuestions}
            type={EngagementSurveyReportTableType.RANGE}
          />
        </div>
      )}
    </div>
  );
};

export default EngagementSurveyReport;
